import React, {useEffect} from "react";
import {TextField} from "@fluentui/react/lib/TextField";
import {ObjectPanelTabs} from "./co-panel/ObjectPanelComponent";
import {CoObjectGeneralInfo, CoObjectSource, CoObjectType, TgkTableObjectSource} from "../interfaces/CoObject";
import {formatMessage} from "../../utils/helpers/LanguageHelper";
import {CoObjectTypeSelector} from "./CoObjectTypeSelector";
import {Toggle} from "@fluentui/react/lib/Toggle";
import {ObjectComponentReferenceInfo} from "./ObjectComponentReferenceInfo";
import {ComponentReference} from "../../api/models/component-reference";

export interface CoObjectsGeneralInfoProps {
  objectInfo: CoObjectGeneralInfo;
  objectSource: CoObjectSource;
  updateCoObjectInfo: (
    obj: CoObjectGeneralInfo,
    tabValid,
    modified: boolean,
    modifiedType?: boolean,
    modifiedSource?: boolean,
  ) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  updateWithComponentData: (component: ComponentReference) => Promise<void>;
  tab: ObjectPanelTabs;
  isFileExcelExisting?: boolean;
}

export const CoObjectsGeneralInfo: React.FunctionComponent<CoObjectsGeneralInfoProps> = (props) => {
  const {
    updateCoObjectInfo,
    isEdit,
    isLocked,
    isComponentInstance,
    updateWithComponentData,
    tab,
    objectInfo,
  } = props;

  useEffect(() => {
    let objInfo =
      objectInfo ??
      ({
        info: null,
        objectName: null,
        objectType: null,
        overrideData: null,
        overrideStyle: null,
      } as CoObjectGeneralInfo);

    let areOverridesSetToDefault = false;

    if (objInfo.objectType === "TABLE" || objInfo.objectType === "CHART") {
      const isOverrideDataNullish = objInfo.overrideData === null || objInfo.overrideData === undefined;
      const isOverrideStyleNullish = objInfo.overrideStyle === null || objInfo.overrideStyle === undefined;

      if (isOverrideDataNullish || isOverrideStyleNullish) {
        // we're editing an old table, we set the overrideData and overrideStyle flags to the default
        areOverridesSetToDefault = true;
        objInfo.overrideData = false;
        objInfo.overrideStyle = false;
      }
    }

    updateCoObjectInfo(objInfo, validateFields(objInfo), areOverridesSetToDefault, false, false);
  }, []);

  function onChangeName(name) {
    const objInfo = {
      ...objectInfo,
      objectName: name,
    };
    updateCoObjectInfo(objInfo, validateFields(objInfo), true);
  }

  function onChangeInfo(info) {
    const objInfo = {
      ...objectInfo,
      info: info,
    };
    updateCoObjectInfo(objInfo, validateFields(objInfo), true);
  }

  function onChangeType(type: CoObjectType | undefined) {
    const objInfo = {
      info: objectInfo ? objectInfo.info : null,
      objectName: objectInfo ? objectInfo.objectName : null,
      objectType: type ?? null,
      overrideData: type === "TABLE" || type === "CHART" ? false : null,
      overrideStyle: type === "TABLE" || type === "CHART" ? false : null,
    };
    updateCoObjectInfo(objInfo, validateFields(objInfo), true, true);
  }

  function validateFields(newObjectInfo: CoObjectGeneralInfo): ObjectPanelTabs {
    let isValid = true;
    if (!newObjectInfo) isValid = false;
    else {
      if (newObjectInfo.objectName == null || newObjectInfo.objectName == "") isValid = false;
      // @ts-ignore
      if (newObjectInfo.objectType == null || newObjectInfo.objectType == "") isValid = false;
    }
    return { key: tab.key, name: tab.name, isValid: isValid };
  }

  // TODO: this section is necessary for the future development (actual DEV-104480)
  const toggleDataIsChecked = (_, isChecked) => {
    updateCoObjectInfo({ ...objectInfo, overrideData: isChecked }, validateFields(objectInfo), true, false, false);
  };

  const toggleStyleIsChecked = (_, isChecked) => {
    updateCoObjectInfo({ ...objectInfo, overrideStyle: isChecked }, validateFields(objectInfo), true, false, false);
  };

  const isKeepDataVisible = props.objectInfo?.objectType == "TABLE";
  const isKeepManualChangesVisibile =
    props.isEdit &&
    (props.objectInfo.objectType == "TABLE" || props.objectInfo.objectType == "CHART") &&
    !props.objectInfo.componentReference &&
    (props.objectSource as TgkTableObjectSource)?.elabResultType !== "IMAGE_STRINGBASE64";

  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <TextField
            id="object_name_edit"
            data-testid={"objectName"}
            label={formatMessage("cof.createnewobject.objectname")}
            placeholder={formatMessage("cof.createnewobject.objectname")}
            required
            value={objectInfo?.objectName}
            onChange={(e) => onChangeName((e.target as HTMLInputElement).value)}
            disabled={isLocked}
          />
        </div>
      </div>
      {isComponentInstance && (
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ObjectComponentReferenceInfo
              componentReference={objectInfo.componentReference}
            ></ObjectComponentReferenceInfo>
          </div>
        </div>
      )}
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <CoObjectTypeSelector
            disabled={isEdit}
            onCoObjectTypeChange={(objectType: CoObjectType | undefined) => {

              console.log("selected objectType: ", objectType);
              onChangeType(objectType);
            }}
            updateWithComponentData={updateWithComponentData}
            coObjectGeneralInfo={objectInfo}
          />
        </div>
      </div>
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <TextField
            id="object_additional_info_edit"
            data-testid={"additionalInfo"}
            label={formatMessage("cof.base.additionalinfo")}
            multiline
            rows={3}
            placeholder={formatMessage("cof.base.additionalinfo")}
            value={objectInfo?.info}
            description={formatMessage("cof.base.optional")}
            onChange={(e) => onChangeInfo((e.target as HTMLInputElement).value)}
            disabled={isLocked}
          />
        </div>
      </div>
      {/* TODO: this section is necessary for the future development (actual DEV-104480) */}
      {isKeepManualChangesVisibile ? (
        <div className="ms-Grid-row" style={{ marginTop: "12px" }}>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <div>{formatMessage("cof.editobject.keepchanges.message")}</div>
            {isKeepDataVisible && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
                <div>{formatMessage("cof.editobject.keepchanges.data")}</div>
                <Toggle
                  checked={objectInfo.overrideData}
                  onChange={toggleDataIsChecked}
                  styles={{ root: { marginBottom: 0 } }}
                  disabled={isLocked}
                />
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
              <div>{formatMessage("cof.editobject.keepchanges.style")}</div>
              <Toggle
                checked={objectInfo.overrideStyle}
                onChange={toggleStyleIsChecked}
                styles={{ root: { marginBottom: 0 } }}
                disabled={isLocked}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
